// Simple image load state hook: idle -> loading -> loaded | error
// Caches image elements for instant re-show without re-attaching listeners.
import { useEffect, useState } from 'react';

// Lightweight image state & cache with simple LRU eviction and optional
// generated tiny blurred placeholder (post-load). Designed for instant
// re-display while bounding memory.

const store = new Map(); // src -> { phase, img, width, height, listeners:Set, placeholder }

// LRU order: most recent at end.
const lru = [];

// Tuning constants (conservative to avoid memory spikes while still caching recent history)
const MAX_IMAGES = 80;        // hard cap of cached images
const PROTECT_RECENT = 15;    // always keep this many most-recent even if over cap
const MIN_EVICT_BATCH = 4;    // try to evict at least this many when trimming

function touch(src) {
  const idx = lru.indexOf(src);
  if (idx !== -1) lru.splice(idx, 1);
  lru.push(src);
}

function evictIfNeeded() {
  if (store.size <= MAX_IMAGES) return;
  // Keep the most recent PROTECT_RECENT.
  const protectedSet = new Set(lru.slice(-PROTECT_RECENT));
  let removed = 0;
  // Iterate from oldest.
  for (let i = 0; i < lru.length && store.size > MAX_IMAGES; i++) {
    const src = lru[i];
    if (protectedSet.has(src)) continue;
    const meta = store.get(src);
    // Do not evict if actively observed (listener attached) or still loading.
    if (!meta || meta.listeners.size > 0 || meta.phase === 'loading') continue;
    // Safe to evict.
    try { meta.img = null; } catch { /* ignore */ }
    store.delete(src);
    lru.splice(i, 1); i--; // adjust index after removal
    removed++;
    if (removed >= MIN_EVICT_BATCH && store.size <= MAX_IMAGES) break;
  }
}

function ensure(src) {
  if (!store.has(src)) {
    store.set(src, { phase: 'idle', img: null, width: undefined, height: undefined, listeners: new Set(), placeholder: undefined });
    touch(src);
    evictIfNeeded();
  } else {
    touch(src);
  }
  return store.get(src);
}

export function getImageMeta(src) {
  return store.get(src);
}

function generatePlaceholder(meta) {
  if (!meta || meta.placeholder || !meta.img) return; // already have or cannot generate yet
  const img = meta.img;
  try {
    const target = 16; // tiny dimension cap
    const ratio = img.naturalWidth / (img.naturalHeight || 1);
    let w = target, h = Math.round(target / ratio);
    if (h > target) { h = target; w = Math.round(target * ratio); }
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, w, h);
    // Mild blur via second draw scaled
    try {
      ctx.globalAlpha = 0.5;
      ctx.drawImage(canvas, 0, 0, w, h);
      ctx.globalAlpha = 1;
    } catch { /* ignore */ }
    meta.placeholder = canvas.toDataURL('image/jpeg', 0.6);
  } catch {
    // Cross-origin taint or other failure: fallback to solid average-like color
    try {
      // Derive a pseudo average color by hashing dimensions
      const r = (img.naturalWidth * 31) % 255;
      const g = (img.naturalHeight * 17) % 255;
      const b = ((img.naturalWidth + img.naturalHeight) * 13) % 255;
      const canvas = document.createElement('canvas');
      canvas.width = 1; canvas.height = 1;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = `rgb(${r},${g},${b})`;
      ctx.fillRect(0,0,1,1);
      meta.placeholder = canvas.toDataURL('image/png');
    } catch { /* ignore entirely */ }
  }
}

function start(src) {
  const meta = ensure(src);
  if (meta.phase === 'loaded' || meta.phase === 'error' || meta.phase === 'loading') return meta;
  meta.phase = 'loading';
  const img = new Image();
  meta.img = img;
  img.onload = () => {
    meta.phase = 'loaded';
    meta.width = img.naturalWidth;
    meta.height = img.naturalHeight;
    // Generate placeholder for future instant masking if none provided.
    generatePlaceholder(meta);
    meta.listeners.forEach(l => l());
  };
  img.onerror = () => {
    meta.phase = 'error';
    meta.listeners.forEach(l => l());
  };
  img.src = src;
  return meta;
}

export function prefetchImage(src) {
  if (!src) return;
  const meta = ensure(src);
  start(src);
}

export function useImageLoadState(src) {
  const [phase, setPhase] = useState(() => (src ? (ensure(src).phase) : 'idle'));
  const [dims, setDims] = useState(() => {
    const meta = src ? store.get(src) : null;
    return meta ? { width: meta.width, height: meta.height } : { width: undefined, height: undefined };
  });
  const [placeholder, setPlaceholder] = useState(() => {
    const meta = src ? store.get(src) : null; return meta?.placeholder;
  });
  useEffect(() => {
    if (!src) return;
    const meta = ensure(src);
    const update = () => {
      setPhase(meta.phase);
      if (meta.width && meta.height) setDims({ width: meta.width, height: meta.height });
      if (meta.placeholder && !placeholder) setPlaceholder(meta.placeholder);
    };
    meta.listeners.add(update);
    // Kick off if needed
    start(src);
    update();
    return () => meta.listeners.delete(update);
  }, [src]);
  return { phase, placeholder, ...dims };
}
